import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, User, X } from "lucide-react";
import api from "../lib/api";
import { useStable } from "../context/StableContext";
import { Input } from "../components/ui/input";

const CAP_LABEL = {
  proprietario: "Proprietario",
  allievo: "Allievo",
  istruttore: "Istruttore",
  staff: "Staff",
};

const initials = (name) =>
  (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "?";

// Directory list presentation only: show "Cognome Nome" (falls back to display_name for legacy
// records without a last_name). Does NOT touch persisted first_name/last_name/display_name.
const listName = (p) => {
  const ln = (p.last_name || "").trim();
  const fn = (p.first_name || "").trim();
  return [ln, fn].filter(Boolean).join(" ") || p.display_name || "Senza nome";
};

export default function People() {
  const { current } = useStable();
  const navigate = useNavigate();
  const [all, setAll] = useState(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("active");

  useEffect(() => {
    if (!current?.id) return;
    let alive = true;
    setAll(null); setError(false);
    api.get(`/stables/${current.id}/persons/directory`, { params: { archived: tab === "archived" } })
      .then((r) => { if (alive) setAll(r.data || []); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [current?.id, tab]);

  const filtered = useMemo(() => {
    if (!all) return null;
    const s = q.trim().toLowerCase();
    if (!s) return all;
    const cf = s.replace(/\s+/g, "").toUpperCase();
    return all.filter((p) =>
      (p.display_name || "").toLowerCase().includes(s) ||
      (p.email || "").toLowerCase().includes(s) ||
      (p.phone || "").toLowerCase().includes(s) ||
      (p.codice_fiscale || "").includes(cf)
    );
  }, [all, q]);

  return (
    <div className="max-w-3xl mx-auto px-1 sm:px-0" data-testid="people-page">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-[#1B3B2B]">Persone</h1>
        <p className="text-sm text-[#5F6B64]">Le persone collegate a {current?.name}.</p>
      </div>

      <div className="mb-4 inline-flex rounded-lg border border-[#E5DEC9] bg-white p-0.5" data-testid="people-tabs">
        {[["active", "Attive"], ["archived", "Archiviate"]].map(([k, lbl]) => (
          <button key={k} data-testid={`people-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-1.5 text-sm rounded-md transition-colors ${tab === k ? "bg-[#1B3B2B] text-white" : "text-[#5F6B64] hover:text-[#1B3B2B]"}`}>
            {lbl}
          </button>
        ))}
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9AA39C]" />
        <Input data-testid="people-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca nome, email, telefono, CF…" className="pl-9" autoFocus={false} />
        {q && (
          <button onClick={() => setQ("")} data-testid="people-search-clear"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9AA39C] hover:text-[#1B3B2B]">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {error ? (
        <div className="text-center py-16 text-[#5F6B64]" data-testid="people-error">
          Impossibile caricare le persone. Riprova.
        </div>
      ) : all === null ? (
        <div className="space-y-2" data-testid="people-loading">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded-xl bg-[#F0EAE1]/50 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-[#5F6B64]" data-testid="people-empty">
          {all.length === 0 ? "Nessuna persona collegata a questa scuderia." : "Nessun risultato per la ricerca."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="people-list">
          {filtered.map((p) => (
            <button key={p.id} data-testid={`person-row-${p.id}`} onClick={() => navigate(`/persone/${p.id}`)}
              className="w-full flex items-center gap-3 rounded-xl border border-[#E5DEC9] bg-white px-3 py-3 text-left hover:border-[#8C532B]/50 transition-colors">
              <span className="h-10 w-10 shrink-0 rounded-full bg-[#1B3B2B] text-white flex items-center justify-center text-xs font-semibold">
                {initials(listName(p))}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-medium text-[#1B3B2B] truncate">{listName(p)}</span>
                <span className="mt-1 flex flex-wrap gap-1.5">
                  {(p.capabilities || []).map((c) => (
                    <span key={c} className="text-[11px] px-1.5 py-0.5 rounded bg-[#F0EAE1] text-[#5F6B64]">
                      {CAP_LABEL[c] || c}
                    </span>
                  ))}
                  {p.account && <span className="text-[11px] px-1.5 py-0.5 rounded border border-[#8C532B]/30 text-[#8C532B]">Account</span>}
                </span>
              </span>
              {(p.email || p.phone) && (
                <span className="hidden sm:block text-xs text-[#9AA39C] text-right max-w-[40%] truncate">
                  {p.email || p.phone}
                </span>
              )}
              <User className="h-4 w-4 text-[#C9C2B4] shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
